import React, {
    useState,
    useRef,
    useContext,
    KeyboardEvent,
    useMemo,
    useCallback,
    useEffect,
} from 'react';

import { Popover } from '@mui/material';
import { IEmojiData } from 'emoji-picker-react';

import TextInput from '../../../../../components/inputs/textInput';
import EmojiPicker from '../../../../../components/shared/EmojiPicker/EmojiPicker';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

//context
import { EditorContext } from '../../../../../context/EditorContext';

import useVariantCreateMutation from 'hooks/variants/useVariantCreateMutation';

//models
import { LabelModel, UnlabelledLabel } from '../../../../../models/label.model';

//components
import LabelHistoryMain from '../../LabelHistory/LabelHistoryMain';

interface AddAlternateListItemProps {
    formulaId: number;
    selectedElement?: { [key: string]: any };
    setAlternateCreateError?: (value: string | null) => void;
    isEditable?: boolean;
    isAddOrEditOnly?: boolean;
    handleAddNewAlternate?: (text: string) => void;
    isLoading?: boolean;
}
const AddAlternateListItem: React.FC<AddAlternateListItemProps> = ({
    formulaId,
    selectedElement,
    setAlternateCreateError,
    isEditable,
    isAddOrEditOnly,
    handleAddNewAlternate,
    isLoading,
}) => {
    const [inputText, setInputText] = useState('');

    const [isOpen, setIsOpen] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const { editorDetails, setEditorDetails } = useContext(EditorContext);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputText(e.target.value);
    };

    const label = useMemo<LabelModel | undefined>(() => {
        return editorDetails.selectedLabel?.id !== UnlabelledLabel.id
            ? editorDetails.selectedLabel
            : undefined;
    }, [editorDetails]);

    const mutation = useVariantCreateMutation(
        selectedElement?.elementId,
        formulaId,
        label,
    );

    const handleSubmit = () => {
        if (setAlternateCreateError) setAlternateCreateError(null);
        mutation.mutate(
            {
                content: inputText,
                label_id: editorDetails.selectedLabel?.id,
                component_id: selectedElement?.elementId,
            },
            {
                onSuccess: (res: any) => {
                    setEditorDetails({
                        ...editorDetails,
                        newly_created_alternate_id: res.data?.id,
                    });
                },
                onError: (err: any) => {
                    if (setAlternateCreateError) {
                        // check that the error is a string. Otherwise, extract the `msg` from it.
                        if (err.detail) {
                            if (typeof err.detail === 'string') {
                                setAlternateCreateError(err.detail);
                            } else if (
                                Array.isArray(err.detail) &&
                                err.detail.length > 0 &&
                                err.detail[0].msg
                            ) {
                                setAlternateCreateError(err.detail[0].msg);
                            } else if (err.detail.msg) {
                                setAlternateCreateError(err.detail.msg);
                            } else {
                                setAlternateCreateError(
                                    'Alternate creation error',
                                );
                            }
                        }
                    }
                },
            },
        );
        setInputText('');
    };
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            if (isAddOrEditOnly) {
                handleAddNewAlternate?.(inputText);
                setIsOpen(false);
                setInputText('');
            } else {
                handleSubmit();
            }
        }
    };

    const onEmojiSelect = (event: React.MouseEvent, emoji: IEmojiData) => {
        if (inputRef.current) {
            const cursorPosition = (inputRef.current as any).selectionStart;
            const textBeforeCursor = inputText.substring(0, cursorPosition);
            const textAfterCursor = inputText.substring(
                cursorPosition,
                inputText.length,
            );
            const updatedText =
                textBeforeCursor + emoji.emoji + textAfterCursor;
            setInputText(updatedText);
            setAnchorEl(null);
        }
    };

    const handleClick = useCallback(() => {
        if (!isLoading) {
            setIsOpen(true);
        }
    }, [isLoading]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node) &&
                !inputText &&
                !anchorEl
            ) {
                setIsOpen(false);
                setAnchorEl(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [anchorEl, inputText]);

    return (
        <div ref={containerRef}>
            {isEditable && (
                <>
                    {' '}
                    {!isOpen && (
                        <TextContainer>
                            <Text onClick={handleClick}>
                                + Add more alternates
                            </Text>
                            <LabelHistoryMain
                                formulaId={formulaId}
                                selectedElement={selectedElement}
                            ></LabelHistoryMain>
                        </TextContainer>
                    )}{' '}
                    {isOpen && (
                        <>
                            <StyledTextInput
                                value={inputText}
                                onChange={handleInputChange}
                                inputRef={inputRef}
                                trailingIcon={<HoverableMdMood size={24} />}
                                trailingIconAction={() =>
                                    setAnchorEl(inputRef.current)
                                }
                                inputStyle={{ width: '100%' }}
                                onKeyDown={handleKeyDown}
                                autoFocus={isOpen}
                            />
                            <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={() => setAnchorEl(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                            >
                                <EmojiPicker onEmojiClick={onEmojiSelect} />
                            </Popover>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const TextContainer = styled.div({
    padding: aampeTheme.spacing4(2, 4),
    display: 'flex',
    justifyContent: 'space-between',
});
const Text = styled.div({
    color: aampeTheme.colors.solidA01,
    fontSize: aampeTheme.size.font.default,
    fontWeight: 400,
    lineHeight: '24px',
    cursor: 'pointer',
});

const StyledTextInput = styled(TextInput)({
    '& .MuiInputBase-root': {
        backgroundColor: aampeTheme.colors.white,
        borderRadius: 'none',
        borderColor: aampeTheme.colors.blue1000,
        minHeight: '32px',
        height: '40px',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: aampeTheme.spacing4(4),
        border: 'none',
    },
    '& .MuiOutlinedInput-input': {
        padding: '0',
        border: 'none',
    },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            borderTop: `1px solid ${aampeTheme.colors.blue1000}`,
            borderBottom: `1px solid ${aampeTheme.colors.blue1000}`,
        },
    },
});

const HoverableMdMood = styled(ICONS.emoji)`
    font-size: 16px;
    margin-top: ${aampeTheme.spacing(0.5)};
    color: ${aampeTheme.colors.gray950};
    transition: color 0.3s, background 0.3s;
    &:hover {
        color: black;
        background: ${aampeTheme.colors.backgroundWG02};
    }
`;
export default AddAlternateListItem;
